import { gql } from '@apollo/client';
import {
  AiSurveySummaryFragment,
  QuestionDefinitionFragment,
  ProcessReportRespondentDetailFragment,
  ReportFragment,
  ProcessReportFragment,
} from './fragments';

const messagesQuery = gql`
  query getMessages($channelId: ID!) {
    messages(channelId: $channelId) {
      id
      text
      createdAt
      isLast
      createdBy
      ... on FreeTextAnswer {
        question {
          definition {
            ...QuestionDefinition
          }
        }
      }
      ... on SelectAnswer {
        value
        question {
          definition {
            ...QuestionDefinition
          }
        }
      }
      ... on ScaleAnswer {
        value
        statementIndex
        comment
        question {
          definition {
            ...QuestionDefinition
          }
        }
      }
      anonymityLevel
      ... on Question {
        definition {
          ...QuestionDefinition
          optional
        }
      }
    }
  }
  ${QuestionDefinitionFragment}
`;

const channelQuery = gql`
  query getChannel($channelId: ID!) {
    channel(channelId: $channelId) {
      id
      anonymityLevel
      anonymityLocked
      isFinished
      isOpen
      createdAt
      endsAt
      topicName
      allowedLanguages {
        id
        code
      }
      allowedAnonymities
      reactions {
        id
        channelId
        messageId
        respondentId
        respondentFullName
        text
        createdAt
      }
      respondent {
        firstname
        surname
        user {
          username
          id
        }
        contacts {
          value
          type
          source
        }
        organization {
          id
          name
        }
      }
    }
  }
`;

const channelSlugForIdNumberQuery = gql`
  query getChannelSlugForIdNumber($surveyId: ID!, $respondentIdNo: String!) {
    getChannelSlugForIdNumber(surveyId: $surveyId, respondentIdNo: $respondentIdNo)
  }
`;

const isAccessTokenValidQuery = gql`
  query isAccessTokenValid {
    isAccessTokenValid {
      expireAt
      status
      consent
      user {
        id
        marketingConsent
        username
        firstname
        systemRole
        surname
        summaryNotifications
        organization {
          id
          name
          systemName
          primaryLanguageCode
        }
        respondents {
          id
          firstname
          surname
          organizationRole
          isLeader
          isReportRecipient
          newAnswersNotifications
          language {
            id
            code
          }
          contacts {
            value
            type
            source
          }
          channels {
            id
            isOpen
            isFinished
            createdAt
            sendAt
            endsAt
            topicName
            surveyFinished
            disabled
            isFinishedByRespondent
            containsOriginalAnonymity
            reactions {
              id
            }
          }
          reports {
            accessKey
            survey {
              sendAt
              endsAt
            }
            topicName
            surveyGroup {
              from
              to
            }
            unreadMessages {
              count
              from
            }
          }
          organization {
            id
            name
            systemName
            productVersionHistory {
              product
              licenceCount
              validFrom
              validTo
            }
            companyId
            primaryLanguageCode
            languages {
              code
            }
            processNotificationsDefault
            processNotificationsLocked
            featureNames
          }
        }
      }
    }
  }
`;

const channelOverviewQuery = gql`
  query channelOverview($channelSlug: ID!, $languageCode: String) {
    channelOverview(channelSlug: $channelSlug, languageCode: $languageCode) {
      respondents {
        id
        firstname
        surname
        organizationRole
        isLeader
        isReportRecipient
        newAnswersNotifications
        channels {
          id
          isOpen
          isFinished
          createdAt
          sendAt
          endsAt
          topicName
          surveyFinished
          disabled
          isFinishedByRespondent
          containsOriginalAnonymity
          reactions {
            id
          }
        }
        reports {
          accessKey
          survey {
            sendAt
            endsAt
          }
          topicName
          surveyGroup {
            from
            to
          }
          unreadMessages {
            count
            from
          }
        }
        organization {
          id
          name
          processNotificationsDefault
          processNotificationsLocked
          featureNames
        }
      }
      user {
        id
        username
        firstname
        surname
        intercomHash
        summaryNotifications
      }
    }
  }
`;

const reportSequenceRespondentDetailQuery = gql`
  query ReportSequenceRespondentDetailQuery(
    $languageCode: String
    $accessToken: String!
    $publicAccess: Boolean
    $respondentId: ID!
    $seqDate: String
  ) {
    getProcessReportRespondentDetail(
      languageCode: $languageCode
      accessToken: $accessToken
      publicAccess: $publicAccess
      respondentId: $respondentId
      seqDate: $seqDate
    ) {
      ...ProcessReportRespondentDetail
    }
  }
  ${ProcessReportRespondentDetailFragment}
`;
const secureRespondentDetailQuery = gql`
  query SecureRespondentDetail($languageCode: String, $accessToken: String!, $seqDate: String) {
    getSecureRespondentDetail(languageCode: $languageCode, accessToken: $accessToken, seqDate: $seqDate) {
      ...ProcessReportRespondentDetail
    }
  }
  ${ProcessReportRespondentDetailFragment}
`;

const surveyPublicInfoQuery = gql`
  query surveyPublicInfo($surveyId: ID!, $langCode: String) {
    surveyPublicInfo(surveyId: $surveyId, langCode: $langCode) {
      id
      topicGroupName
      organizationName
      endsAt
      status
    }
  }
`;

const getComparsionDataForProcessReport = gql`
  query getComparsionDataForProcessReport(
    $accessToken: String!
    $periods: [DateRangeInput!]!
    $teamsFilter: [String!]
  ) {
    getComparsionDataForProcessReport(accessToken: $accessToken, periods: $periods, teamFilters: $teamsFilter) {
      id
      stepStatistics {
        periodIndex
        stepId
        avgMetric
        statistics {
          withoutProblems {
            denominator
            numerator
          }
          responseRate {
            numerator
            denominator
          }
          anonymityRate {
            numerator
            denominator
          }
          finishedRate {
            numerator
            denominator
          }
        }
      }
    }
  }
`;

const surveyGroupRespondents = gql`
  query getProcessReportRespondents(
    $accessToken: String!
    $addedOnFrom: Date
    $addedOnTo: Date
    $teamsFilter: [String!]
    $pagination: Pagination
    $searchQuery: String
    $positionIds: [ID!]
    $recruiterIds: [ID!]
    $managerIds: [ID!]
    $withoutDeleted: Boolean
    $showOnlyUnread: Boolean
  ) {
    getProcessReportRespondents(
      accessToken: $accessToken
      addedOnFrom: $addedOnFrom
      addedOnTo: $addedOnTo
      teamFilters: $teamsFilter
      pagination: $pagination
      searchQuery: $searchQuery
      positionIds: $positionIds
      recruiterIds: $recruiterIds
      managerIds: $managerIds
      withoutDeleted: $withoutDeleted
      showOnlyUnread: $showOnlyUnread
    ) {
      data {
        metricQuestionsForRespondent {
          respondentId
          answers {
            topicGroupId
            value
          }
        }
        creditsToReturn {
          respondentId
          creditsToReturn
        }
        respondents {
          id
          preferredContactType
          firstname
          surname
          deleted
          contacts {
            type
            source
            value
            phoneNumber {
              countryCallingCode
              nationalNumber
            }
          }
        }
        addedOn
        surveys {
          id
          status
          sendAt
          endsAt
          topicGroup {
            id
          }
          respondentIds
          publicChannels {
            id
            respondentId
            lastRemindedAt
            maxAnonymousHighlightedLevel
            withAnswer
            isFinishedByRespondent
            finishedAt
          }
        }
        hiringSurveys {
          id
          status
          sendAt
          endsAt
          topicGroup {
            id
          }
          respondentIds
          publicChannels {
            id
            respondentId
            lastRemindedAt
            maxAnonymousHighlightedLevel
            withAnswer
            isFinishedByRespondent
            finishedAt
          }
        }
        hiringAdditionalData {
          respondentId
          position
          isHired
          isRejected
          recruiterName
        }
        deletedRespondents
        undeliveredInvitations {
          respondentId
          undeliveredSurveyInvitations {
            surveyId
            contact
            status
          }
        }
        hasUnreadMessages {
          respondentId
          unreadMessages
        }
      }
      totalCount
    }
  }
`;

const lastMessagesForRespondent = gql`
  query lastMessagesForRespondent($surveyIds: [ID!]!, $respondentIds: [ID!]!, $accessToken: String) {
    lastMessagesForRespondent(surveyIds: $surveyIds, respondentIds: $respondentIds, accessToken: $accessToken) {
      id
      messageStatus
      processed
      createdAt
      error
      surveyId
      respondentId
    }
  }
`;

const reportQuery = gql`
  query getReport(
    $languageCode: String
    $accessToken: String!
    $reportDefinitionId: ID
    $topicGroupId: ID
    $addedOnFrom: Date
    $addedOnTo: Date
    $publicAccess: Boolean
  ) {
    getReport(
      languageCode: $languageCode
      accessToken: $accessToken
      reportDefinitionId: $reportDefinitionId
      topicGroupId: $topicGroupId
      addedOnFrom: $addedOnFrom
      addedOnTo: $addedOnTo
      publicAccess: $publicAccess
    ) {
      ...ReportFragment
    }
  }
  ${ReportFragment}
`;

const reportSequenceQuery = gql`
  query getProcessReport(
    $languageCode: String
    $accessToken: String!
    $addedOnFrom: Date
    $addedOnTo: Date
    $publicAccess: Boolean
    $teamsFilter: [String!]
    $positionIds: [ID!]
    $recruiterIds: [ID!]
    $managerIds: [ID!]
    $withoutDeleted: Boolean
    $showOnlyUnread: Boolean
  ) {
    getProcessReport(
      languageCode: $languageCode
      accessToken: $accessToken
      addedOnFrom: $addedOnFrom
      addedOnTo: $addedOnTo
      publicAccess: $publicAccess
      teamFilters: $teamsFilter
      positionIds: $positionIds
      recruiterIds: $recruiterIds
      managerIds: $managerIds
      withoutDeleted: $withoutDeleted
      showOnlyUnread: $showOnlyUnread
    ) {
      ...ProcessReportFragment
    }
  }
  ${ProcessReportFragment}
`;

const aiSurveySummaryQuery = gql`
  query aiSurveySummaryQuery(
    $languageCode: String!
    $accessToken: String!
    $systemMessage: String
    $answers: [QuestionWithAnswer!]
  ) {
    aiSurveySummary(
      languageCode: $languageCode
      accessToken: $accessToken
      systemMessage: $systemMessage
      answers: $answers
    ) {
      ...AiSurveySummaryFragment
    }
  }
  ${AiSurveySummaryFragment}
`;

export const reportDefinitionsNewForSurveyQuery = gql`
  query ReportDefinitionsNewForSurvey($surveyId: ID!) {
    reportDefinitionsNewForSurvey(surveyId: $surveyId) {
      id
      name
      createdAt
      createdBy
      isPrimary
    }
  }
`;

export const reportDefinitionsNewForTopicQuery = gql`
  query ReportDefinitionsNewForTopic($surveyId: ID!) {
    reportDefinitionsNewForTopic(surveyId: $surveyId) {
      id
      name
      createdAt
      createdBy
      isPrimary
    }
  }
`;

export const reportDefinitionNewForTopicQuery = gql`
  query ReportDefinitionNewForTopic($surveyId: ID!, $id: ID!) {
    reportDefinitionNewForTopic(surveyId: $surveyId, id: $id) {
      id
      name
      createdAt
      createdBy
      isPrimary
      definition
    }
  }
`;

export const reportDefinitionNewForSurveyQuery = gql`
  query ReportDefinitionNewForSurvey($surveyId: ID!, $id: ID!) {
    reportDefinitionNewForSurvey(surveyId: $surveyId, id: $id) {
      id
      name
      createdAt
      createdBy
      isPrimary
      definition
    }
  }
`;

const getNewReportQuery = gql`
  query GetNewReport($accessToken: String!, $languageCode: String, $publicAccess: Boolean) {
    getNewReport(accessToken: $accessToken, languageCode: $languageCode, publicAccess: $publicAccess) {
      id
      reportRoots
      totalRespondents
      teams {
        id
        teamName
        directMembers
        memberTeams
        teamLeader
      }
      anonymityEnabled
      anonymityForced
      anonymitySettings {
        default
        allowedLevels
      }
      surveys {
        public
        status
      }
      questions {
        id
        index
        reportIndex
        text
        type
        selectedRange
        answersLocal
        statementsLocal
        showEscapeOption
        isRankingScale
        metrics
        reportOrder
        answersToHighlight
      }
      responses {
        id
        respondentId
        surveyId
        topicGroupId
        anonymous
        finished
        additionallyAnonymized
        originalAnonymous
        messages {
          id
          questionDefinitionIndex
          statementIndex
          text
          value
          comment
          relevance
          anonymous
          originalAnonymous
          date
        }
      }
      respondents {
        id
        firstname
        surname
        addedOn
      }
      metadata {
        surveyStart
        surveyEnd
        status
        organizationDepth
        language {
          id
          code
        }
        adminEmails
        topicGroupId
        organization {
          id
          name
          systemName
          featureNames
          languages {
            code
          }
        }
      }
      topicGroup {
        translations {
          language {
            code
          }
          value
          description
        }
      }
      questionLabels {
        questionDefinitionIndex
        questionLabelComments {
          label
          commentIds
        }
      }
      surveyId
      reportDefinition {
        id
        definition
      }
    }
  }
`;

export {
  aiSurveySummaryQuery,
  channelOverviewQuery,
  channelQuery,
  channelSlugForIdNumberQuery,
  getComparsionDataForProcessReport,
  getNewReportQuery,
  isAccessTokenValidQuery,
  lastMessagesForRespondent,
  messagesQuery,
  reportSequenceRespondentDetailQuery,
  surveyGroupRespondents,
  surveyPublicInfoQuery,
  secureRespondentDetailQuery,
  reportQuery,
  reportSequenceQuery,
};
